import { AlertCircle, AlertTriangle, TrendingDown } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { differenceInDays } from "date-fns";
import { calcularStatus } from "../types";
import type { ContaPagarResumoDto, ContaReceberResumoDto } from "../types";

interface FinanceAlertsProps {
  contasPagar: ContaPagarResumoDto[];
  contasReceber: ContaReceberResumoDto[];
  saldoPrevisto: number;
}

export const FinanceAlerts = ({
  contasPagar,
  contasReceber,
  saldoPrevisto,
}: FinanceAlertsProps) => {
  const today = new Date();
  
  // Contas vencidas
  const contasVencidasPagar = contasPagar.filter(
    c => calcularStatus(c.isPago, c.dtVencimento) === "atrasado"
  );

  const contasVencidasReceber = contasReceber.filter(
    c => calcularStatus(c.isPago, c.dtVencimento) === "atrasado"
  );

  // Contas vencendo nos próximos 7 dias
  const contasVencendoPagar = contasPagar.filter(c => {
    if (calcularStatus(c.isPago, c.dtVencimento) !== "pendente") return false;
    const diasParaVencimento = differenceInDays(new Date(c.dtVencimento), today);
    return diasParaVencimento >= 0 && diasParaVencimento <= 7;
  });

  const contasVencendoReceber = contasReceber.filter(c => {
    if (calcularStatus(c.isPago, c.dtVencimento) !== "pendente") return false;
    const diasParaVencimento = differenceInDays(new Date(c.dtVencimento), today);
    return diasParaVencimento >= 0 && diasParaVencimento <= 7;
  });

  const alerts = [];

  // Alerta de saldo negativo previsto
  if (saldoPrevisto < 0) {
    alerts.push({
      variant: "destructive" as const,
      icon: TrendingDown,
      title: "Saldo Previsto Negativo",
      description: `O saldo previsto está negativo em ${Math.abs(saldoPrevisto).toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}. Planeje recebimentos ou reduza despesas.`,
    });
  }

  // Alerta de contas a pagar vencidas
  if (contasVencidasPagar.length > 0) {
    const totalVencido = contasVencidasPagar.reduce((sum, c) => sum + c.valorSaldo, 0);
    alerts.push({
      variant: "destructive" as const,
      icon: AlertCircle,
      title: "Contas a Pagar Vencidas",
      description: `${contasVencidasPagar.length} conta(s) vencida(s) totalizando ${totalVencido.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}. Providencie o pagamento.`,
    });
  }

  // Alerta de contas a receber vencidas
  if (contasVencidasReceber.length > 0) {
    const totalVencido = contasVencidasReceber.reduce((sum, c) => sum + c.valorSaldo, 0);
    alerts.push({
      variant: "destructive" as const,
      icon: AlertCircle,
      title: "Contas a Receber Vencidas",
      description: `${contasVencidasReceber.length} conta(s) vencida(s) totalizando ${totalVencido.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}. Entre em contato com os clientes.`,
    });
  }

  // Alerta de contas a pagar vencendo
  if (contasVencendoPagar.length > 0) {
    const totalVencendo = contasVencendoPagar.reduce((sum, c) => sum + c.valorSaldo, 0);
    alerts.push({
      variant: "default" as const,
      icon: AlertTriangle,
      title: "Contas a Pagar Vencendo",
      description: `${contasVencendoPagar.length} conta(s) vencendo nos próximos 7 dias totalizando ${totalVencendo.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}.`,
    });
  }

  // Alerta de contas a receber vencendo
  if (contasVencendoReceber.length > 0) {
    const totalVencendo = contasVencendoReceber.reduce((sum, c) => sum + c.valorSaldo, 0);
    alerts.push({
      variant: "default" as const,
      icon: AlertTriangle,
      title: "Contas a Receber Vencendo",
      description: `${contasVencendoReceber.length} conta(s) vencendo nos próximos 7 dias totalizando ${totalVencendo.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}.`,
    });
  }

  if (alerts.length === 0) {
    return null;
  }

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold text-foreground">Alertas Financeiros</h2>
      <div className="grid gap-4">
        {alerts.map((alert, index) => {
          const Icon = alert.icon;
          return (
            <Alert key={index} variant={alert.variant}>
              <Icon className="h-4 w-4" />
              <AlertTitle>{alert.title}</AlertTitle>
              <AlertDescription>{alert.description}</AlertDescription>
            </Alert>
          );
        })}
      </div>
    </div>
  );
};
